import { Auth } from 'aws-amplify'
import { current, original } from 'immer'

import TagManager from 'react-gtm-module'
import { Vault } from 'shared'
import { getCognitoLUPIds, getCognitoUserId, getLocationData, USER_STORE_KEY } from './helpers'
import type { TGooseClaim, TIdToken, TReducerActions, TSessionContextValues } from './types'

const actions: TReducerActions = {
  // Is the initial application state when is loaded for first time
  // INITIALIZING

  LOGIN: (state: TSessionContextValues, _?: GenericSimpleBag) => {
    state.status = 'LOGIN'
  },
  NAVIGATE: (state: TSessionContextValues, payload?: GenericSimpleBag) => {
    const loc = getLocationData(state.user?.claims || [], payload!.locationName)
    if (loc) {
      state.currentLocation = loc
    } else {
      state.currentLocation = state.user!.claims[0].locations[0]
    }
    Vault.setObj(USER_STORE_KEY, { user: state.user, currentLocation: state.currentLocation })
    state.status = 'AUTHENTICATED'
  },

  // Message generated by SessionProvider when AWS Amplify send the `signIn` event after
  // a successfully login
  AUTHENTICATE: (state: TSessionContextValues, payload?: GenericSimpleBag) => {
    const accessToken = payload!.user.signInUserSession.accessToken.jwtToken
    const decodedIdToken = payload!.user.signInUserSession.idToken.payload as TIdToken
    const claims = decodedIdToken.goose_api_claims
      ? (JSON.parse(decodedIdToken.goose_api_claims) as TGooseClaim[])
      : undefined
    const username = decodedIdToken.display_name || decodedIdToken.email
    const email = decodedIdToken.email
    const userId = getCognitoUserId(email, decodedIdToken['cognito:groups'])
    const lupIds = getCognitoLUPIds(email, decodedIdToken['cognito:groups'])
    const lupId = lupIds?.find((l) => l.locationName === payload!.currentLocation)?.lupId

    if (!userId || !lupId) {
      state.user = undefined
      Vault.remove(USER_STORE_KEY)
      state.status = 'DEAUTHENTICATED'
      return
    }

    TagManager.dataLayer({
      dataLayer: {
        event: 'LOGIN_SUCCESS',
        user: {
          id: userId,
          lupId: lupId,
        },
      },
    })
    state.user = {
      token: accessToken,
      id: decodedIdToken.sub,
      idHash: decodedIdToken.intercom_user_hash,
      claims: claims || [],
      username,
      email,
      userId,
      lupId,
    }
    state.userLUPs = lupIds

    Vault.setObj(USER_STORE_KEY, {
      user: state.user,
      currentLocation: state.currentLocation,
      userLUPs: state.userLUPs,
    })
    state.status = 'AUTHENTICATED'
  },

  // the application reloaded and the user is already authenticated
  AUTHENTICATED: (state: TSessionContextValues, _?: GenericSimpleBag) => {
    state.status = 'AUTHENTICATED'
  },

  // Message sent when the app finished initializing and is ready
  READY: (state: TSessionContextValues, _?: GenericSimpleBag) => {
    state.status = 'READY'
  },

  // Message generated by SessionProvider when AWS Amplify send the `signOut` event after
  // a successfully logout
  DEAUTHENTICATE: (state: TSessionContextValues, _?: GenericSimpleBag) => {
    if (state.user?.userId) {
      TagManager.dataLayer({
        dataLayer: {
          event: 'LOGOUT_HEADER',
          user: {
            id: state.user.userId,
          },
        },
      })
    }

    state.user = undefined
    state.userLUPs = []
    Vault.remove(USER_STORE_KEY)
    state.status = 'DEAUTHENTICATED'
  },

  // Send when the application request a logout
  SIGNOUT: (state: TSessionContextValues, _?: GenericSimpleBag) => {
    state.status = 'SIGNOUT'
    // SessionProvider setup a handler with AWS Auth and will dispatch
    // a DEAUTHENTICATE event on the signOut event
    Auth.signOut()
      // eslint-disable-next-line no-console
      .catch((err: Error) => console.log(err))

    actions['DEAUTHENTICATE'](state)
  },

  CHANGE_LOCATION: (state: TSessionContextValues, payload?: GenericSimpleBag) => {
    state.user!.lupId = payload!.lupId
  },
}

/* eslint-disable no-console */
export const sessionReducer = (state: TSessionContextValues, action: TReducerAction) => {
  const fx = actions[action.type]
  fx?.(state, action.payload)

  /* istanbul ignore next */
  if (process.env.NODE_ENV === 'development') {
    console.group(action.type)
    if (!fx) {
      console.log('%c action not found ', 'background: #FF0000; color: #fff')
    }
    console.log('start', original(state))
    console.log('payload', action.payload)
    console.log('after', current(state))
    console.groupEnd()
  }
}
/* eslint-enable no-console */
